import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import {
  Plus,
  Search,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Calendar,
  User,
  Building,
  ExternalLink,
  Filter,
  Trash2,
  Square,
  CheckSquare
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import useOpsBriefingLink from "../hooks/useOpsBriefingLink";
import { OpsBriefingBookButton, OpsBriefingLinkEditor } from "../components/intake/OpsBriefingBooking";

const STATUS_CONFIG = {
  pending: { label: "Awaiting Activation", color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: Clock },
  converted: { label: "Activated", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-600 border-gray-200", icon: XCircle }
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", { 
    month: "short", 
    day: "numeric", 
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

export default function IntakeSubmissions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Ops pre-kickoff briefing calendar — booking button for reps, editor for admins.
  const opsBriefing = useOpsBriefingLink();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  async function fetchSubmissions() {
    try {
      const response = await apiClient.get("/intake/submissions");
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }

  const filteredSubmissions = submissions.filter(s => {
    if (filterStatus !== "all" && s.status !== filterStatus) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return s.project_name.toLowerCase().includes(searchLower) ||
             s.client_name.toLowerCase().includes(searchLower) ||
             s.client_email.toLowerCase().includes(searchLower);
    }
    return true;
  });

  // Stats
  const stats = {
    total: submissions.length,
    pending: submissions.filter(s => s.status === "pending").length,
    converted: submissions.filter(s => s.status === "converted").length
  };

  // Bulk selection helpers
  const allSelected = filteredSubmissions.length > 0 && selectedIds.length === filteredSubmissions.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < filteredSubmissions.length;

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSubmissions.map(s => s.id));
    }
  }

  function toggleSelect(id) {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }

  async function handleBulkDelete() {
    setDeleting(true);
    try {
      // Delete each selected submission
      await Promise.all(selectedIds.map(id => 
        apiClient.delete(`/intake/submissions/${id}`)
      ));
      toast.success(`${selectedIds.length} submission(s) deleted`);
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete some submissions");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="intake-submissions" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            My Submissions
          </h1>
          <p className="text-muted-foreground mt-1">Track your intake submissions</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <OpsBriefingBookButton link={opsBriefing} variant="outline" />
          <Button onClick={() => navigate("/sales/intake/new")} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            New Intake
          </Button>
        </div>
      </div>

      {/* Admin-only: where the ops briefing calendar URL gets pasted. */}
      <OpsBriefingLinkEditor link={opsBriefing} />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileText className="w-4 h-4" />
              <span className="text-xs">Total Submissions</span>
            </div>
            <p className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span className="text-xs">Awaiting Activation</span>
            </div>
            <p className="text-2xl font-light text-yellow-600" style={{ fontFamily: 'var(--font-sans)' }}>{stats.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-xs">Activated</span>
            </div>
            <p className="text-2xl font-light text-green-600" style={{ fontFamily: 'var(--font-sans)' }}>{stats.converted}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by project or client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Awaiting Activation</SelectItem>
            <SelectItem value="converted">Activated</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {selectedIds.length > 0 && (
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete ({selectedIds.length})
          </Button>
        )}
      </div>

      {/* Submissions Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox 
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all"
                    className={someSelected ? "data-[state=checked]:bg-primary/50" : ""}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>No submissions found</p>
                    <Button onClick={() => navigate("/sales/intake/new")} className="mt-4 rounded-full">
                      <Plus className="w-4 h-4 mr-2" /> Create First Intake
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubmissions.map(submission => {
                  const StatusIcon = STATUS_CONFIG[submission.status]?.icon || Clock;
                  const isSelected = selectedIds.includes(submission.id);
                  return (
                    <TableRow key={submission.id} className={`cursor-pointer hover:bg-secondary/30 ${isSelected ? 'bg-primary/5' : ''}`}>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(submission.id)}
                          aria-label={`Select ${submission.project_name}`}
                          data-testid={`select-${submission.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{submission.project_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {submission.project_city}, {submission.project_state}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{submission.client_name}</p>
                          <p className="text-xs text-muted-foreground">{submission.client_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{submission.service_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {formatDate(submission.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_CONFIG[submission.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[submission.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {submission.program_id && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => navigate(`/programs/${submission.program_id}`)}
                              title="View Program"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" /> Delete Submissions
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} submission(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive hover:bg-destructive/90" 
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
